import { useCallback } from 'react';

import { PointCreateModalPages } from 'application/components/modals/PointCreateModal/types';
import {
  useBillingRepositoryInjector,
  usePaymentMethodRepositoryInjector,
  usePointRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { ModalTypes } from 'application/providers/ModalProvider';
import AnalyticsService, {
  EventNameGTM,
  ParamNameAMP,
  ParamNameGTM,
  PointEventNameAMP,
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
} from 'application/services/AnalyticsService';
import CompanyUtils from 'application/utils/CompanyUtils';
import { useAccountContext } from 'application/providers/AccountProvider';
import { PointWithDevice } from 'application/presenters/point/types';

import { Country, UserFeatures } from 'domain/api/graphql/generated';

import { UseActionButtonsViewModel, UseActionButtonsViewModelProps } from './types';

const useActionButtonsViewModel = ({
  targetStreamId,
  collectionId,
  isTemplateMode,
  selectedPoints,
  setInFlow,
  replaceModal,
  redirectToStreamCategoryPage,
  setIsStreamBindButtonLoading,
  showCopyStreamError,
  showCopyStreamWithThematicError,
  showCopyStreamWithThematicSuccess,
  copyTemplateStream,
  getSavedStreams,
  getStream,
  onboardingPassedRequest,
  refetchFeaturesOnboarding,
  readStreamWithThematic,
  handleChangeStream,
}: UseActionButtonsViewModelProps): UseActionButtonsViewModel => {
  const { currentCompany, companyIds } = useAccountContext();

  const { readCurrentPoint, setCurrentPoint } = usePointRepositoryInjector();
  const { watchBillingInfo } = useBillingRepositoryInjector();
  const { readPaymentMethod } = usePaymentMethodRepositoryInjector();

  const { canRealizeBillingOperation, hasActiveLicenses, hasInactiveLicenses } = watchBillingInfo(currentCompany!.id);

  const isDenyCreatePoint = CompanyUtils.isDenyCreatePoint(canRealizeBillingOperation, currentCompany);

  const { paymentMethod } = readPaymentMethod(currentCompany!.id);
  const isNeedPayment = (hasActiveLicenses && !paymentMethod) || hasInactiveLicenses;

  const handleAddPointClick = () => {
    AnalyticsService.event(PointEventNameAMP.POINT_CREATE_BUTTON_CLICK, {
      [PointParamNameAMP.FROM_PAGE]: PointCreateModalPages.STREAM,
    });

    setInFlow(!isDenyCreatePoint);

    if (isDenyCreatePoint) {
      replaceModal(ModalTypes.DENY_OPERATION, {});
    } else {
      replaceModal(ModalTypes.POINT_CREATE, {});
    }
  };

  const sendEvents = (streamId?: string) => {
    AnalyticsService.eventGTM(EventNameGTM.STREAM_START, {
      [ParamNameGTM.ACCOUNT_CREATED_DATE]: currentCompany?.created,
    });

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BOUND, {
      [ParamNameAMP.STREAM_ID]: streamId,
      [ParamNameAMP.COLLECTION_ID]: collectionId,
    });
  };

  const onBindPointsClick = async () => {
    let streamId = targetStreamId;
    const withThematic = readStreamWithThematic();

    if (isTemplateMode) {
      try {
        streamId = (await copyTemplateStream(streamId!, currentCompany!.id, withThematic))!;

        if (withThematic) {
          showCopyStreamWithThematicSuccess();
        }
      } catch (error) {
        if (withThematic) {
          showCopyStreamWithThematicError();
          return;
        }

        showCopyStreamError(currentCompany?.country || Country.Ru);
        return;
      }
    }

    await handleChangeStream(streamId!);
    await getSavedStreams(companyIds);

    if (isTemplateMode) {
      redirectToStreamCategoryPage(streamId!);
    }

    sendEvents(streamId);

    if (targetStreamId) {
      await getStream(targetStreamId);
    }

    const oldPoint = readCurrentPoint();
    setCurrentPoint({ ...oldPoint, stream: { id: streamId || '' } } as PointWithDevice);
  };

  const bindStream = async () => {
    setIsStreamBindButtonLoading(true);

    await onBindPointsClick();

    const isOk = await onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

    if (!isOk) {
      throw new Error();
    }

    await refetchFeaturesOnboarding();

    setIsStreamBindButtonLoading(false);
  };

  const isShowPaymentModal = () => {
    const currentPoint = readCurrentPoint();
    const isSettingsFinished = !!(currentPoint?.stream?.id && currentPoint.device?.type);
    const showPaymentModal = isNeedPayment && isSettingsFinished && currentPoint.isNew;

    return showPaymentModal;
  };

  const handleBindStreamClick = useCallback(async () => {
    await bindStream();

    if (isShowPaymentModal()) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {
        selectedPoints,
      });
    }
  }, [bindStream, isShowPaymentModal, replaceModal]);

  return {
    handleAddPointClick,
    handleBindStreamClick,
  };
};

export default useActionButtonsViewModel;
